"use client";

import type Branch from "branch-sdk";
import type { ComponentProps } from "react";
import { createContext } from "react";

import { useRequestMe } from "../../data/requests/me/useRequestMe";
import { URL_GIFT_IMAGE } from "../constants/urls/urlGift.constants";
import { URL_ONBOARDING } from "../constants/urls/urlOnboarding";
import { URL_SELF } from "../constants/urls/urlSelf";
import { URL_TRIVIA_IMAGE } from "../constants/urls/urlTrivia";
import { URL_THE_WORD_IMAGE } from "../constants/urls/urlWord";
import type { BibleBookId } from "../types/schema/bible/bibleManifestSchema";

export type BranchLinkCreator = {
  bible: (
    linkProps: Branch.DeepLinkData & {
      translation: string;
      book: BibleBookId;
      chapter: number;
      verse?: number;
    },
  ) => Promise<string>;
  campaign: (linkProps: BranchLinkById) => Promise<string>;
  categories: (linkProps: BranchLinkByDate) => Promise<string>;
  collection: (linkProps: BranchLinkById) => Promise<string>;
  community: (linkProps: BranchLinkByIdAndName) => Promise<string>;
  dailyQuote: (linkProps: BranchLinkByIdAndUrl) => Promise<string>;
  general: (linkProps?: Branch.DeepLinkData) => Promise<string>;
  gift: (linkProps: BranchLinkByPromoCode) => Promise<string>;
  link: (linkProps: Branch.DeepLinkData) => Promise<string>;
  prayer: (linkProps: BranchLinkById) => Promise<string>;
  radioStation: (linkProps: BranchLinkById) => Promise<string>;
  trivia: (linkProps: BranchLinkByDate) => Promise<string>;
  theWord: (linkProps: BranchLinkByDate) => Promise<string>;
};

export const BranchContext = createContext<BranchLinkCreator>({
  bible: () => Promise.resolve(""),
  campaign: () => Promise.resolve(""),
  categories: () => Promise.resolve(""),
  collection: () => Promise.resolve(""),
  community: () => Promise.resolve(""),
  dailyQuote: () => Promise.resolve(""),
  general: () => Promise.resolve(""),
  gift: () => Promise.resolve(""),
  link: () => Promise.resolve(""),
  prayer: () => Promise.resolve(""),
  radioStation: () => Promise.resolve(""),
  trivia: () => Promise.resolve(""),
  theWord: () => Promise.resolve(""),
});

type BranchLinkById = Branch.DeepLinkData & {
  id: number;
};

type BranchLinkByIdAndName = BranchLinkById & {
  name: string;
};

type BranchLinkByIdAndUrl = BranchLinkById & {
  url?: string;
};

type BranchLinkByDate = Branch.DeepLinkData & {
  date: string | Date;
};

export type BranchLinkByPromoCode = Branch.DeepLinkData & {
  promoCode: string;
};

export const BranchProvider = ({
  children,
  ...props
}: Omit<ComponentProps<typeof BranchContext.Provider>, "value">) => {
  const { query: user } = useRequestMe();

  const link = async (linkProps: Branch.DeepLinkData) => {
    try {
      const response = await fetch("https://api2.branch.io/v1/url", {
        method: "POST",
        body: JSON.stringify({
          branch_key:
            process.env.NEXT_PUBLIC_BRANCH_KEY ??
            process.env.DOPPLER_PUBLIC_BRANCH_KEY,
          ...linkProps,
        }),
      });
      const body = await response.json();
      if (response.ok) return body.url;
      throw body.error.message;
    } catch (e) {
      console.error(e);
      // no-op otherwise
    }
    return linkProps.data.$fallback_url;
  };

  const general = async (linkProps?: Branch.DeepLinkData) => {
    const code = user?.data?.referral_code ?? "no-referral-code";
    const path = `${URL_ONBOARDING}${user?.data?.referral_code ? `?referrer=${user.data.referral_code}` : ""}`;
    return link({
      campaign: code,
      channel: "Referral",
      data: {
        $canonical_identifier: `share-${code}`,
        $deeplink_path: path,
        $fallback_url: path,
      },
      feature: "Referral",
      ...linkProps,
    });
  };

  const gift = async ({ promoCode, ...linkProps }: BranchLinkByPromoCode) => {
    const url = new URL(`https://hallow.com/redeemmobile/gift/${promoCode}`);
    return link({
      campaign: "Gift Hallow",
      channel: "Referral",
      feature: "Referral",
      data: {
        $canonical_identifier: `gift-${user.data?.id}`,
        $deeplink_path: url.pathname,
        $fallback_url: url.href,
        $og_image_url: URL_GIFT_IMAGE,
      },
      ...linkProps,
    });
  };

  const dailyQuote = async ({
    id,
    url,
    data,
    ...linkProps
  }: BranchLinkByIdAndUrl) => {
    const path = new URL(url ?? "https://hallow.com/daily-quote/");
    return link({
      campaign: "Daily Quote",
      channel: "Referral",
      data: {
        $canonical_identifier: `${user.data?.id ?? null}-daily-quote-${id}`,
        $deeplink_path: path.pathname,
        $fallback_url: path.href,
        ...data,
      },
      feature: "Referral",
      ...linkProps,
    });
  };

  const categories = async ({ date, data, ...linkProps }: BranchLinkByDate) => {
    return link({
      campaign: `categories-game-${new Date(date).toDateString()}`,
      channel: "Referral",
      data: {
        $deeplink_path: "/categories",
        $fallback_url: `${URL_SELF}/categories`,
        $og_image_url:
          "https://images.hallow.app/games/categories-game/0_l.webp",
        ...data,
      },
      feature: "Categories",
      ...linkProps,
    });
  };

  const trivia = async ({ date, data, ...linkProps }: BranchLinkByDate) => {
    return link({
      campaign: `trivia-${new Date(date).toDateString()}`,
      channel: "Referral",
      data: {
        $deeplink_path: "/trivia",
        $fallback_url: `${URL_SELF}/trivia`,
        $og_image_url: URL_TRIVIA_IMAGE,
        ...data,
      },
      feature: "Trivia",
      ...linkProps,
    });
  };

  const theWord = async ({ date, data, ...linkProps }: BranchLinkByDate) => {
    return link({
      campaign: `the-word-${new Date(date).toDateString()}`,
      channel: "Referral",
      data: {
        $deeplink_path: "/the-word",
        $fallback_url: `${URL_SELF}/the-word`,
        $og_image_url: URL_THE_WORD_IMAGE,
        ...data,
      },
      feature: "The Word",
      ...linkProps,
    });
  };

  const prayer = async ({ id, data, ...linkProps }: BranchLinkById) => {
    const path = `/prayers/${id}?is_shared=true`;
    return link({
      feature: "Prayer",
      campaign: `Prayer - ${id}`,
      data: {
        $canonical_identifier: `${user?.data?.id}-prayer-${id}`,
        $deeplink_path: path,
        $fallback_url: `${URL_SELF}${path}`,
        ...data,
      },
      ...linkProps,
    });
  };

  const bible = async ({
    translation,
    book,
    chapter,
    verse,
    data,
    ...linkProps
  }: Branch.DeepLinkData & {
    translation: string;
    book: BibleBookId;
    chapter: number;
    verse?: number;
  }) => {
    const path = `/bible/${translation}/${book}/${chapter}${verse ? `/${verse}` : ""}?is_shared=true`;
    return link({
      feature: "Bible",
      campaign: `Bible - ${book} ${chapter}${verse ? `:${verse}` : ""}`,
      data: {
        $canonical_identifier: `${user?.data?.id}-bible-${translation}-${book}-${chapter}${verse ? `-${verse}` : ""}`,
        $deeplink_path: path,
        $fallback_url: `${URL_SELF}${path}`,
        ...data,
      },
      ...linkProps,
    });
  };

  const community = async ({
    id,
    name,
    data,
    ...linkProps
  }: BranchLinkByIdAndName) => {
    const path = `/community/${id}`;
    return link({
      feature: "Community",
      campaign: `Community - ${name}`,
      data: {
        $canonical_identifier: `${user?.data?.id}-community-${id}`,
        $deeplink_path: path,
        $fallback_url: `${URL_SELF}${path}`,
        ...data,
      },
      ...linkProps,
    });
  };

  const collection = async ({ id, data, ...linkProps }: BranchLinkById) => {
    const path = `/collections/${id}?is_shared=true`;
    return link({
      feature: "Collection",
      campaign: `Collection - ${id}`,
      data: {
        $canonical_identifier: `${user?.data?.id}-collection-${id}`,
        $deeplink_path: path,
        $fallback_url: `${URL_SELF}${path}`,
        ...data,
      },
      ...linkProps,
    });
  };

  const radioStation = async ({ id, data, ...linkProps }: BranchLinkById) => {
    const path = `/radio-stations/${id}`;
    return link({
      feature: "Radio Station",
      campaign: `Radio Station - ${id}`,
      data: {
        $canonical_identifier: `${user?.data?.id}-radio-stations-${id}`,
        $deeplink_path: path,
        $fallback_url: `${URL_SELF}${path}`,
        ...data,
      },
      ...linkProps,
    });
  };

  const campaign = async ({ id, data, ...linkProps }: BranchLinkById) => {
    const path = `/campaigns/${id}`;
    return link({
      feature: "Campaign",
      campaign: `Campaign - ${id}`,
      data: {
        $canonical_identifier: `${user?.data?.id}-campaign-${id}`,
        $deeplink_path: path,
        $fallback_url: `${URL_SELF}${path}`,
        ...data,
      },
      ...linkProps,
    });
  };

  return (
    <BranchContext.Provider
      value={{
        bible,
        campaign,
        categories,
        collection,
        community,
        dailyQuote,
        general,
        gift,
        link,
        prayer,
        radioStation,
        trivia,
        theWord,
      }}
      {...props}
    >
      {children}
    </BranchContext.Provider>
  );
};
